/**
 * Shared form validation for admin entities (roles, workflows), so every
 * surface enforces the same rules.
 *
 * Mirrors validate_display_name() / validate_role_description() and the
 * permission and workflow checks in backend/routers/admin.py — keep in sync.
 */

import { z } from 'zod';
import { wallClockToInstant } from '@/utils/timezone';
import { formatNumber, orgFormat } from '@/utils/format';
import { displayDateLong } from '@/utils/dateDisplay';

const NAME_ALLOWED = /^[A-Za-z0-9 ._\-&/]+$/;

/** Shared name rule: trimmed, space-collapsed, at least one letter, safe charset. */
export function validateDisplayName(
  value: string,
  label: string,
  minLen: number,
  maxLen: number,
): string | null {
  const name = value.replace(/\s+/g, ' ').trim();
  if (!name) return `${label} is required`;
  if (name.length < minLen || name.length > maxLen) {
    return `${label} must be between ${minLen} and ${maxLen} characters`;
  }
  if (!/\p{L}/u.test(name)) return `${label} must contain at least one letter`;
  if (!NAME_ALLOWED.test(name)) {
    return `${label} may only contain letters, digits, spaces and . _ - & /`;
  }
  return null;
}

/**
 * A sentence worth showing about a name's capitalisation, or null.
 *
 * Mirrors caps_lock_hint() in backend/utils/validation.py — keep in sync. The
 * backend returns the same text in ApiResponse.warnings; this copy exists so
 * the form can say it BEFORE the round trip, while the field is still focused.
 *
 * 🔴 IT NEVER REWRITES THE VALUE, and nothing that calls it may block a save.
 * "P2 NORTH" and "EV Bay" are deliberate names, and silently title-casing them
 * would be wrong more often than the slip this was written for.
 *
 * DETECTS: mostly-capitals that does not start with a capital — what Caps Lock
 * with Shift produces. Caught live: "bASEMENT LEVEL 01", "fOURTH FLOOR" and
 * "ttESTING" (two lowercase, then capitals, which is why the test is "a capital
 * after a lowercase letter" rather than "the second character is a capital").
 *
 * DOES NOT DETECT a misspelling: "Firat Floor" is a typo for "First Floor" and
 * is ordinary Title Case to any casing rule. The message says so itself.
 */
export function capsLockHint(value: string | null | undefined, label = 'Name'): string | null {
  const name = (value ?? '').replace(/\s+/g, ' ').trim();
  const letters = [...name].filter((c) => /\p{L}/u.test(c));
  if (letters.length < 2) return null;

  // Caseless scripts count zero capitals and so are never flagged.
  const capitals = letters.filter((c) => c !== c.toLowerCase() && c === c.toUpperCase()).length;
  if (capitals * 2 < letters.length) return null;

  let seenLower = false;
  let capitalAfterLower = false;
  for (const c of letters) {
    const isLower = c !== c.toUpperCase() && c === c.toLowerCase();
    const isUpper = c !== c.toLowerCase() && c === c.toUpperCase();
    if (isLower) seenLower = true;
    else if (isUpper && seenLower) { capitalAfterLower = true; break; }
  }
  if (!capitalAfterLower) return null;

  return (
    `${label} "${name}" is mostly capitals but does not start with one, which is what ` +
    'Caps Lock with the Shift key produces. It will be saved exactly as typed. This reads ' +
    'capitalisation only, so it cannot tell you whether a word is spelt wrong.'
  );
}

export const ROLE_NO_PERMISSIONS_ERROR = 'Select at least one permission for this role.';

export type RoleField = 'name' | 'description' | 'permissions';

/** Returns an inline error for an invalid role name, or null when valid. */
export function validateRoleName(value: string): string | null {
  return validateDisplayName(value, 'Role name', 2, 50);
}

/** Description is optional; blank is fine, but junk is not. */
export function validateRoleDescription(value: string): string | null {
  const desc = value.replace(/\s+/g, ' ').trim();
  if (!desc) return null;
  if (desc.length > 200) return 'Description must be 200 characters or fewer';
  if (!/[\p{L}\p{N}]/u.test(desc)) return 'Description must contain letters or numbers';
  return null;
}

export function validateRolePermissions(perms: string[]): string | null {
  return perms.length ? null : ROLE_NO_PERMISSIONS_ERROR;
}

/** Normalised name to send to the API (trimmed, internal spaces collapsed). */
export function normaliseName(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}
export const normaliseRoleName = normaliseName;

// ---------------------------------------------------------------------------
// Organisation name
// ---------------------------------------------------------------------------
// Mirrors validate_org_name() in backend/utils/validation.py — keep in sync.
// Every surface that sets an organisation name shares this: General Settings,
// the super-admin create/rename forms, and registration. A name that differs by
// surface is how "$#@%^" got saved with no message at all (TC_194 / TC_195).

// No allowed-character set. There used to be one: an ASCII-only whitelist of
// letters, digits and a short punctuation list. It refused "@#$%^" as intended
// and also refused Café Coffee Day, भारत टेक, 株式会社テスト and Zürich
// Versicherung, which was not intended. A whitelist cannot tell junk from a
// customer whose language the author did not think of.
//
// \p{L} and \p{N} are Unicode letter and number properties; the u flag is
// required for them. This mirrors str.isalnum() on the backend.
const ORG_NAME_ALNUM = /[\p{L}\p{N}]/u;
// Not text: control characters, surrogates, line/paragraph separators. Cf
// (format) is deliberately absent — it holds the zero-width joiner and
// non-joiner that Devanagari, Bengali and Arabic need.
const ORG_NAME_FORBIDDEN = /[\p{Cc}\p{Cs}\p{Zl}\p{Zp}]/u;
export const ORG_NAME_CHARSET_ERROR =
  'Organisation name must contain at least one letter or number';

/** Trim and collapse repeated internal spaces — this is what gets saved. */
export function normaliseOrgName(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/** Returns an inline error message for an invalid org name, or null when valid. */
export function validateOrgName(value: string): string | null {
  const name = normaliseOrgName(value);
  if (!name) return 'Organisation name is required';
  if (name.length < 2 || name.length > 100) {
    return 'Organisation name must be between 2 and 100 characters';
  }
  if (ORG_NAME_FORBIDDEN.test(name)) {
    return 'Organisation name contains a control character and cannot be used';
  }
  if (!ORG_NAME_ALNUM.test(name)) return ORG_NAME_CHARSET_ERROR;
  return null;
}

// ---------------------------------------------------------------------------
// Routing a backend error onto the form field it refers to
// ---------------------------------------------------------------------------

/*
 * These used to be `xErrorField(message)` helpers that substring-matched the
 * message. That breaks the moment user-supplied text contains a field word,
 * because the duplicate 409s quote the offending value back: a site named
 * "Barcode HQ" produced "A site named 'Barcode HQ' already exists.", which
 * matched `includes('code')` and pushed a duplicate-NAME error onto the Code
 * field. Same class of bug for a role named "Permissions Lead", a workflow
 * named "Trigger Review", a form named "Field Inspection".
 *
 * Routing now works in two tiers:
 *   1. A 422 body names the field in detail[].loc — structured and exact, so it
 *      always wins. This also fixes messages that interpolate raw user input,
 *      e.g. "Unknown timezone: <value>".
 *   2. A 409/400 carries only a sentence. It is matched against server-owned
 *      phrases with every quoted value stripped out first, so user input can
 *      never steer the result.
 * Anything that matches neither returns field: null, and the caller shows the
 * general error instead of guessing a field.
 */

/** Strip '...' and "..." so user-supplied values cannot influence routing. */
export function stripQuotedValues(message: string): string {
  return message.replace(/'[^']*'/g, ' ').replace(/"[^"]*"/g, ' ');
}

/** First meaningful segment of a Pydantic loc: ["body","items",0,"label"] -> "items". */
function locFieldName(loc: unknown): string | null {
  if (!Array.isArray(loc)) return null;
  for (const part of loc) {
    if (typeof part === 'string' && part !== 'body') return part;
  }
  return null;
}

export interface FieldErrorRouting<F extends string> {
  /** Backend field name (from a 422 loc) -> form field. */
  fromLoc?: Record<string, F>;
  /** Server-owned phrase -> form field. Receives the lowercased, de-quoted message. */
  fromMessage?: (deQuoted: string) => F | null;
}

export interface RoutedRequestError<F extends string> {
  message: string;
  /** null when the error cannot be tied to a field — show it generally. */
  field: F | null;
}

/** Resolve a backend error to a message plus, where knowable, the field it belongs to. */
export function routeRequestError<F extends string>(
  err: unknown,
  routing: FieldErrorRouting<F>,
): RoutedRequestError<F> | null {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (!res?.status || res.status < 400) return null;
  const detail = res.data?.detail;

  if (Array.isArray(detail)) {
    const entries = detail as { msg?: unknown; loc?: unknown }[];
    const message = formatDetailList(entries);
    let field: F | null = null;
    for (const entry of entries) {
      const name = locFieldName(entry?.loc);
      const mapped = name ? routing.fromLoc?.[name] : undefined;
      if (mapped) { field = mapped; break; }
    }
    return { message: message || 'The request could not be completed', field };
  }

  if (typeof detail === 'string' && detail) {
    const deQuoted = stripQuotedValues(detail).toLowerCase();
    return { message: detail, field: routing.fromMessage?.(deQuoted) ?? null };
  }

  return { message: 'The request could not be completed', field: null };
}

export interface RoutedFieldErrors<F extends string> {
  /** The whole response, joined — for when nothing maps to a field. */
  message: string;
  /** Every field the response blamed, each with its own message. */
  fields: Partial<Record<F, string>>;
}

/**
 * Like routeRequestError, but keeps EVERY field a 422 blames instead of the
 * first one.
 *
 * A profile save can fail on first_name and last_name at once — clearing both
 * boxes is the ordinary way to do it — and returning one field means the user
 * fixes it, saves, and is told about the other. Two round trips to learn two
 * things the response said together the first time.
 */
export function routeRequestErrors<F extends string>(
  err: unknown,
  routing: FieldErrorRouting<F>,
): RoutedFieldErrors<F> | null {
  const single = routeRequestError(err, routing);
  if (!single) return null;

  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  const fields: Partial<Record<F, string>> = {};

  if (Array.isArray(detail)) {
    for (const entry of detail as DetailItem[]) {
      const name = locFieldName((entry as { loc?: unknown })?.loc);
      const mapped = name ? routing.fromLoc?.[name] : undefined;
      // First message per field wins; pydantic emits at most one per field
      // here, and a later structural entry should not overwrite a specific one.
      if (mapped && !fields[mapped]) {
        fields[mapped] = formatDetailItem(entry) ?? single.message;
      }
    }
  } else if (single.field) {
    fields[single.field] = single.message;
  }

  return { message: single.message, fields };
}

// ---------------------------------------------------------------------------
// Spaces > rooms and desks
// ---------------------------------------------------------------------------
// A room name is unique WITHIN ITS FLOOR — two "Boardroom"s on one floor is
// confusing, the same name on floor 1 and floor 2 is how buildings are actually
// named. The clash comes back as a 409 with a plain-string detail naming the
// room it collided with, so it routes by message rather than by loc.

// ---------------------------------------------------------------------------
// Bookings in the past
// ---------------------------------------------------------------------------

/**
 * The server's sentence, mirrored exactly.
 *
 * backend/services/booking_time_rule.py:PAST_MESSAGE (re-exported by
 * routers/space.py). New Bug Room bookings changed both the words and the rule
 * — see windowIsPast. Kept identical the way validateGstinStrict
 * mirrors validate_gstin: the client answers the common case without a round
 * trip, and when the server does answer, the user sees the same words rather
 * than two phrasings of one rule.
 */
export const PAST_MESSAGE = 'Cannot book a time that has already passed. Please pick a future time.';
/** A chosen desk start in the past (Bug_109) — backend
 *  services/booking_time_rule.py START_PAST_MESSAGE, word for word. */
export const START_PAST_MESSAGE = 'The start time has already passed. Pick a start time from now on.';

/** services/booking_time_rule.py:START_GRACE. A room or parking start may be
 *  at most fifteen minutes ago; the END has no grace (end <= now is past). */
export const START_GRACE_MS = 15 * 60_000;

/**
 * Has this window ALREADY ENDED?
 *
 * The END half of the rule, and on its own the DESK rule for today (desks are
 * booked by the day, with no start grace). Rooms and parking add the start
 * check — see windowIsPast below (New Bug Room bookings). BUG_07's original
 * reading, that a room in progress was always bookable, was replaced there.
 *
 * `end` before `start` means the window runs past midnight and finishes the
 * NEXT day — 22:00-06:00 is ten hours, not a negative fourteen. Same reading as
 * utils/time_windows.booking_window on the server.
 *
 * NOTE ON ZONES. This uses the BROWSER's clock, which is the user's, which is
 * usually the organisation's. The server decides for real, in the
 * organisation's zone, so a user in a different zone from their organisation
 * gets a server answer rather than a wrong local one. That is why this is an
 * early answer and not the rule.
 */
export function windowHasEnded(
  date: string,
  startTime: string,
  endTime: string,
  now: Date = new Date(),
  timeZone?: string,
): boolean {
  if (!date || !endTime) return false;
  // THE ORGANISATION'S CLOCK, mirroring the server's _reject_if_ended. Without
  // the zone this parsed in the BROWSER's, so a viewer one zone away from the
  // building could be told a slot had passed that the server would have taken —
  // or, worse, be waved through and then refused. Omitted zone keeps the
  // browser, which is right when the two coincide and is the app-wide default.
  let end = wallClockToInstant(`${date}T${endTime}`, timeZone);
  if (Number.isNaN(end.getTime())) return false;
  if (startTime && endTime < startTime) {
    // Wrapped past midnight: add the day to the WALL CLOCK, not to the instant,
    // so a DST jump between the two days cannot shift the result by an hour.
    const next = new Date(`${date}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    end = wallClockToInstant(`${next.toISOString().slice(0, 10)}T${endTime}`, timeZone);
  }
  return end.getTime() <= now.getTime();
}

/**
 * The ROOM rule (New Bug Room bookings): past when the END has come, OR the
 * START is more than fifteen minutes ago — services/booking_time_rule
 * window_has_passed. A tester booked 16:30-17:30 after the slot had gone and
 * the old end-only rule accepted it.
 *
 * Desks do NOT use this: they are booked by the day, with no start grace
 * (windowHasEnded plus a day check — see utils/bookingPicker).
 */
export function windowIsPast(
  date: string,
  startTime: string,
  endTime: string,
  now: Date = new Date(),
  timeZone?: string,
): boolean {
  if (windowHasEnded(date, startTime, endTime, now, timeZone)) return true;
  if (!date || !startTime) return false;
  const start = wallClockToInstant(`${date}T${startTime}`, timeZone);
  if (Number.isNaN(start.getTime())) return false;
  return start.getTime() < now.getTime() - START_GRACE_MS;
}

/** The same question for a booking already expressed as instants. */
export function instantHasPassed(end: Date | string, now: Date = new Date()): boolean {
  const at = end instanceof Date ? end : new Date(end);
  if (Number.isNaN(at.getTime())) return false;
  return at.getTime() <= now.getTime();
}

export type BookingFormField = 'date' | 'end_time' | 'end_datetime'
  | 'series_start_date' | 'recurring_start_date' | 'new_end_datetime';

export const BOOKING_ERROR_ROUTING: FieldErrorRouting<BookingFormField> = {
  fromLoc: {
    date: 'date',
    end_time: 'end_time',
    end_datetime: 'end_datetime',
    series_start_date: 'series_start_date',
    recurring_start_date: 'recurring_start_date',
    new_end_datetime: 'new_end_datetime',
  },
  fromMessage: (m) => (m.includes('already passed') ? 'end_time' : null),
};

export type RoomFormField = 'name' | 'room_code';

export const ROOM_ERROR_ROUTING: FieldErrorRouting<RoomFormField> = {
  fromLoc: { name: 'name', room_code: 'room_code' },
  // "A room named 'Boardroom' is already on this floor." — de-quoted and
  // lowercased before it gets here, so match on the words either side of the
  // value rather than on the value.
  fromMessage: (m) => {
    if (m.includes('room named') || m.includes('already on this floor')) return 'name';
    if (m.includes('room_code') || m.includes('room code')) return 'room_code';
    return null;
  },
};

export type DeskFormField = 'name';

export const DESK_ERROR_ROUTING: FieldErrorRouting<DeskFormField> = {
  fromLoc: { name: 'name', desk_code: 'name' },
  fromMessage: (m) => (m.includes('desk coded') || m.includes('already on this floor')
    ? 'name' : null),
};

export type InviteField = 'email' | 'first_name' | 'last_name' | 'role';

export const INVITE_ERROR_ROUTING: FieldErrorRouting<InviteField> = {
  fromLoc: {
    email: 'email',
    first_name: 'first_name',
    last_name: 'last_name',
    role: 'role',
  },
  // "A user with email '<value>' already exists" / "Invalid role: <value>".
  // The role value is interpolated unquoted, so match the email phrases first.
  fromMessage: (m) => {
    // "… already belongs to a contractor portal account." — the one-address-
    // one-principal check across staff, contractor and tenant logins.
    if (m.includes('with email') || m.includes('this email') || m.includes('already belongs to')) return 'email';
    if (m.includes('invalid role')) return 'role';
    return null;
  },
};

// ---------------------------------------------------------------------------
// Settings > Profile
// ---------------------------------------------------------------------------
// Mirrors schemas/auth.py:UserUpdateRequest. The Settings page wrote /auth/me
// straight from useState with no rule of its own and reported every failure as
// `toast.error('Failed to update profile')` — so emptying a name produced a
// red toast that named no field and gave no reason, while the 422 underneath
// said exactly which two fields were wrong and why (TC_05).

export type ProfileField =
  | 'first_name' | 'last_name' | 'phone' | 'preferred_language' | 'timezone'
  | 'department' | 'date_of_birth';

export const PROFILE_ERROR_ROUTING: FieldErrorRouting<ProfileField> = {
  fromLoc: {
    first_name: 'first_name',
    last_name: 'last_name',
    phone: 'phone',
    preferred_language: 'preferred_language',
    timezone: 'timezone',
    // DSH-044: My Profile also sends these two, and their 422s were dropped.
    department: 'department',
    date_of_birth: 'date_of_birth',
  },
  fromMessage: () => null,
};

/** schemas/auth.py UserUpdateRequest.department max_length. */
export const DEPARTMENT_MAX = 128;

export function validateDepartment(value: string | null | undefined): string | null {
  return (value ?? '').length > DEPARTMENT_MAX
    ? `Department must be at most ${DEPARTMENT_MAX} characters.`
    : null;
}

/**
 * utils/validation.py `_birth_date`, word for word: after 1 January 1900 and
 * in the past. `today` is the organisation's ('yyyy-MM-dd', orgToday()), the
 * day the server judges by. Empty means "not set" and passes.
 */
export function validateBirthDate(value: string | null | undefined, today: string): string | null {
  if (!value) return null;
  if (value < '1900-01-01') return 'Date of birth must be after 1 January 1900.';
  if (value >= today) return 'Date of birth must be in the past.';
  return null;
}

// ---------------------------------------------------------------------------
// Change password (DSH-031)
// ---------------------------------------------------------------------------
// Both password forms (Settings > Security, My Profile > Security) toasted a
// bare "Failed to change password" for every refusal, although the server
// says exactly which field is wrong and why. These give each refusal its
// field, and catch the common cases before the request.

/**
 * backend/utils/password_policy.py `_message()`, word for word, so a person is
 * told the same rule by the form and by the server. MIN_LENGTH 8, upper +
 * lower + digit.
 */
export const PASSWORD_RULE_MESSAGE =
  'Password must be at least 8 characters and include an uppercase letter, a lowercase letter and a digit.';
/** routers/auth.py change_password, word for word. */
export const PASSWORD_UNCHANGED_MESSAGE = 'New password must be different from your current password';
export const PASSWORD_MISMATCH_MESSAGE = 'Passwords do not match';

/** password_policy.failure_reason, mirrored. The server still decides. */
export function validateNewPassword(value: string): string | null {
  if (value.length < 8 || !/[A-Z]/.test(value) || !/[a-z]/.test(value) || !/[0-9]/.test(value)) {
    return PASSWORD_RULE_MESSAGE;
  }
  return null;
}

export type PasswordField = 'current_password' | 'new_password' | 'confirm_password';

/** Every check the form can make before sending. Empty when there is nothing to say. */
export function validatePasswordChange(
  current: string, next: string, confirm: string,
): Partial<Record<PasswordField, string>> {
  const out: Partial<Record<PasswordField, string>> = {};
  if (!current) out.current_password = 'Enter your current password';
  const rule = validateNewPassword(next);
  if (rule) out.new_password = rule;
  else if (current && next === current) out.new_password = PASSWORD_UNCHANGED_MESSAGE;
  if (confirm !== next) out.confirm_password = PASSWORD_MISMATCH_MESSAGE;
  return out;
}

export const PASSWORD_ERROR_ROUTING: FieldErrorRouting<PasswordField> = {
  fromLoc: { current_password: 'current_password', new_password: 'new_password' },
  // Server-owned sentences only (see stripQuotedValues above).
  fromMessage: (m) => {
    if (m.includes('current password is incorrect')) return 'current_password';
    if (m.startsWith('password must') || m.startsWith('new password')) return 'new_password';
    return null;
  },
};

export type SftpField = 'host' | 'port' | 'username' | 'password' | 'remote_directory';

export const SFTP_ERROR_ROUTING: FieldErrorRouting<SftpField> = {
  fromLoc: {
    host: 'host',
    port: 'port',
    username: 'username',
    password: 'password',
    remote_directory: 'remote_directory',
  },
  // SFTP errors all come from SFTPConfigRequest as 422s, so loc covers them.
  // The string-detail cases ("SFTP not configured…", "Could not read the SFTP
  // directory…") describe the connection, not one field — show them generally.
  fromMessage: () => null,
};

/** Roles: duplicate name is the only 409; everything else arrives as a 422. */
export const ROLE_ERROR_ROUTING: FieldErrorRouting<RoleField> = {
  fromLoc: { name: 'name', description: 'description', permissions: 'permissions' },
  // "A role named '<value>' already exists in this organisation"
  fromMessage: (m) => (m.includes('role named') ? 'name' : null),
};

// ---------------------------------------------------------------------------
// Workflows (TC_75)
// ---------------------------------------------------------------------------

export const WORKFLOW_NO_ACTIONS_ERROR =
  'Add at least one action — a workflow with no actions does nothing.';

export type WorkflowField = 'name' | 'trigger_event' | 'conditions' | 'actions';

export function validateWorkflowName(value: string): string | null {
  return validateDisplayName(value, 'Workflow name', 2, 100);
}

export function validateWorkflowTrigger(value: string, known: readonly string[]): string | null {
  const trigger = value.trim();
  if (!trigger) return 'Select a trigger event for this workflow';
  if (!known.includes(trigger)) return `Unknown trigger event: ${trigger}`;
  return null;
}

export function validateWorkflowActions(
  actions: Array<{ action_type: string; config?: Record<string, unknown> }>,
  known: readonly string[],
): string | null {
  if (!actions.length) return WORKFLOW_NO_ACTIONS_ERROR;
  const bad = actions.find((a) => !known.includes((a.action_type || '').trim()));
  if (bad) return `Unknown action type: ${bad.action_type || '(blank)'}`;
  // A notify action with nobody to notify. The server refuses this too — this
  // is the courtesy, not the gate — but the recipient is now picked from a
  // control on the form, so the answer is here and a round trip is not needed
  // to learn that a required box was left empty.
  const i = actions.findIndex(
    (a) => a.action_type === 'notify' && !String(a.config?.to ?? '').trim());
  return i === -1 ? null
    : `Action ${i + 1} notifies somebody — choose who. A rule notifies a specific person.`;
}

export function validateWorkflowConditions(
  conditions: Array<{ field: string }>,
): string | null {
  return conditions.some((c) => !(c.field || '').trim())
    ? 'Every condition needs a field name'
    : null;
}

/** Map a backend 4xx message onto the workflow field it refers to. */
export const WORKFLOW_ERROR_ROUTING: FieldErrorRouting<WorkflowField> = {
  fromLoc: {
    name: 'name',
    trigger_event: 'trigger_event',
    conditions: 'conditions',
    actions: 'actions',
  },
  // "A workflow named '<value>' already exists."
  fromMessage: (m) => (m.includes('workflow named') ? 'name' : null),
};

// ---------------------------------------------------------------------------
// Custom forms (TC_89)
// ---------------------------------------------------------------------------

export const FORM_NO_FIELDS_ERROR = 'Add at least one field — a form with no fields is unusable.';

export type FormBuilderField = 'form_name' | 'module' | 'fields';

export interface FormFieldLike {
  label: string;
  type: string;
  options?: string[] | null;
}

export function validateFormName(value: string): string | null {
  return validateDisplayName(value, 'Form name', 2, 100);
}

export function validateFormModule(value: string, known: readonly string[]): string | null {
  const module = value.trim().toLowerCase();
  if (!module) return 'Module is required';
  if (!known.includes(module)) return `Unknown module: ${value}`;
  return null;
}

/** Per-row errors keyed by field index, for the field builder rows. */
/** The placeholder a new builder field used to carry ("Field 4") — backend
 *  routers/admin.py PLACEHOLDER_FIELD_LABEL. Never a label. */
export const PLACEHOLDER_FIELD_LABEL = /^field[\s_-]*\d+$/i;

export function validateFormFieldRows(
  fields: FormFieldLike[],
  knownTypes: readonly string[],
): Record<number, string> {
  const rowErrors: Record<number, string> = {};
  const seen = new Map<string, number>();
  fields.forEach((f, i) => {
    const label = (f.label || '').replace(/\s+/g, ' ').trim();
    if (!label) {
      rowErrors[i] = 'Label is required';
    } else if (label.length > 100) {
      rowErrors[i] = 'Label must be between 1 and 100 characters';
    } else if (!/\p{L}/u.test(label)) {
      rowErrors[i] = 'Label must contain at least one letter';
    } else if (PLACEHOLDER_FIELD_LABEL.test(label)) {
      // Pallavi Bugs 2 and 5: "Field 4" is the builder's placeholder, saved
      // because nobody replaced it; the server refuses it too.
      rowErrors[i] = 'Give this field a label that says what it asks';
    } else if (!knownTypes.includes(f.type)) {
      rowErrors[i] = `Unknown field type: ${f.type || '(blank)'}`;
    } else if (f.type === 'select' && !(f.options || []).some((o) => (o || '').trim())) {
      rowErrors[i] = 'Dropdown needs at least one option';
    } else if (seen.has(label.toLowerCase())) {
      rowErrors[i] = `Duplicate field label: '${label}'`;
    }
    if (!rowErrors[i]) seen.set(label.toLowerCase(), i);
  });
  return rowErrors;
}

/** Form-level error for the fields section (empty list only). */
export function validateFormFields(fields: FormFieldLike[]): string | null {
  return fields.length ? null : FORM_NO_FIELDS_ERROR;
}

/** Map a backend 4xx message onto the form-builder field it refers to. */
export const FORM_ERROR_ROUTING: FieldErrorRouting<FormBuilderField> = {
  fromLoc: { form_name: 'form_name', module: 'module', fields: 'fields' },
  // "A form named '<value>' already exists."
  fromMessage: (m) => (m.includes('form named') ? 'form_name' : null),
};

// ---------------------------------------------------------------------------
// Custom form field machine keys (TC_89 follow-up)
// ---------------------------------------------------------------------------

const FIELD_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
export const FIELD_NAME_ERROR =
  'Use lowercase letters, numbers and underscores only, starting with a letter.';

/** Format rules for a field's machine key. Returns an error, or null. */
export function validateFieldName(value: string): string | null {
  const name = value || '';
  if (!name.trim()) return 'Field name is required';
  if (name.length > 64) return 'Field name must be 64 characters or fewer';
  if (name.startsWith('_') || name.endsWith('_')) {
    return 'Field name must not start or end with an underscore';
  }
  if (!FIELD_NAME_PATTERN.test(name)) return FIELD_NAME_ERROR;
  return null;
}

/** "Visitor Name" -> "visitor_name". Returns '' when nothing usable remains. */
export function slugifyFieldName(label: string): string {
  const slug = (label || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_')
    .slice(0, 64)
    .replace(/_+$/, '');
  // Must start with a letter.
  return /^[a-z]/.test(slug) ? slug : slug ? `f_${slug}`.slice(0, 64).replace(/_+$/, '') : '';
}

/** Slugify and suffix (_2, _3 …) until the key is unique among `taken`. */
export function uniqueFieldName(label: string, taken: readonly string[]): string {
  const base = slugifyFieldName(label) || 'field';
  const used = new Set(taken.map((t) => (t || '').toLowerCase()));
  if (!used.has(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base}_${n}`.slice(0, 64).replace(/_+$/, '');
    if (!used.has(candidate)) return candidate;
  }
  return base;
}

/**
 * Per-row errors for field machine keys.
 * `legacyNames` are the keys already stored on the form being edited — those
 * skip the format check so pre-existing forms stay editable. Uniqueness is
 * always enforced.
 */
export function validateFieldNameRows(
  fields: Array<{ name: string }>,
  legacyNames?: readonly string[],
): Record<number, string> {
  const rowErrors: Record<number, string> = {};
  const legacy = new Set((legacyNames ?? []).map((n) => n));
  const seen = new Set<string>();
  fields.forEach((f, i) => {
    const key = (f.name || '').trim().toLowerCase();
    if (seen.has(key)) {
      rowErrors[i] = `Duplicate field name: '${f.name}'. Each field needs its own key.`;
      return;
    }
    seen.add(key);
    if (legacy.has(f.name)) return; // untouched legacy key
    const err = validateFieldName(f.name);
    if (err) rowErrors[i] = err;
  });
  return rowErrors;
}

// ---------------------------------------------------------------------------
// Lookup / notification lists (TC_100)
// ---------------------------------------------------------------------------

export const LOOKUP_MAX_ITEMS = 500;
export const LOOKUP_MAX_ITEM_LEN = 200;
export const LOOKUP_NO_ITEMS_ERROR = 'Add at least one item to this list.';

export type LookupListField = 'list_name' | 'items';

export function validateLookupListName(value: string): string | null {
  return validateDisplayName(value, 'List name', 2, 100);
}

/** Split the textarea into cleaned items: trimmed, blank lines dropped. */
export function parseLookupItems(raw: string): string[] {
  return raw.split('\n').map((s) => s.trim()).filter(Boolean);
}

/**
 * Validate the parsed items. Items are free-text tokens, not emails —
 * length, blank and duplicate rules only.
 */
export function validateLookupItems(raw: string): string | null {
  const items = parseLookupItems(raw);
  if (!items.length) return LOOKUP_NO_ITEMS_ERROR;
  if (items.length > LOOKUP_MAX_ITEMS) return `A list may hold at most ${LOOKUP_MAX_ITEMS} items`;
  const tooLong = items.find((i) => i.length > LOOKUP_MAX_ITEM_LEN);
  if (tooLong) {
    return `Item '${tooLong.slice(0, 40)}…' is too long (max ${LOOKUP_MAX_ITEM_LEN} characters)`;
  }
  const seen = new Set<string>();
  for (const item of items) {
    const key = item.toLowerCase();
    if (seen.has(key)) return `Duplicate item: '${item}'`;
    seen.add(key);
  }
  return null;
}

/** Map a backend 4xx message onto the list field it refers to. */
export const LOOKUP_ERROR_ROUTING: FieldErrorRouting<LookupListField> = {
  fromLoc: { list_name: 'list_name', items: 'items' },
  // "A list named '<value>' already exists." The old rule also matched a bare
  // "already exists", which swallowed the per-item "Duplicate item: '<label>'"
  // 422 and showed it under List name instead of Items.
  // "The list '<name>' already holds N items that this save was not made
  // from" (deep-test ADM-008) belongs under List name too.
  fromMessage: (m) => (m.includes('list named') || m.includes('already holds') ? 'list_name' : null),
};

// ---------------------------------------------------------------------------
// Notification templates (TC_112)
// ---------------------------------------------------------------------------

/** Canonical event types. Mirrors _NOTIFICATION_EVENT_TYPES in backend/routers/admin.py. */
export const NOTIFICATION_EVENTS: { value: string; suggested_vars: string[] }[] = [
  { value: 'ticket_assigned', suggested_vars: ['ticket.id', 'ticket.title', 'ticket.priority', 'ticket.status', 'user.name', 'user.email'] },
  { value: 'ticket_resolved', suggested_vars: ['ticket.id', 'ticket.title', 'user.name'] },
  { value: 'wo_created', suggested_vars: ['wo.id', 'wo.title', 'user.name'] },
  { value: 'wo_assigned', suggested_vars: ['wo.id', 'wo.title', 'wo.priority', 'wo.due_date', 'user.name'] },
  { value: 'wo_completed', suggested_vars: ['wo.id', 'wo.title', 'user.name'] },
  { value: 'lease_expiring', suggested_vars: ['lease.id', 'lease.property_name', 'lease.end_date'] },
  { value: 'visitor_checked_in', suggested_vars: ['visitor.name', 'host.name'] },
  { value: 'user.created', suggested_vars: ['user.first_name', 'user.email', 'organisation.name'] },
];

export const NOTIFICATION_CHANNELS = ['email', 'sms', 'whatsapp', 'push'] as const;

/**
 * Deep-test ADM-038: the (event, channel) pairs something SENDS with a
 * template. Mirrors _TEMPLATES_THAT_SEND in backend/routers/admin.py, which
 * refuses to create any other. Eight events on four channels were offered and
 * only the ticket-assigned email was ever read.
 */
export const TEMPLATES_THAT_SEND: ReadonlyArray<readonly [string, string]> = [['ticket_assigned', 'email']];
export const templateIsUsed = (event: string, channel: string) =>
  TEMPLATES_THAT_SEND.some(([e, c]) => e === event && c === channel);

/** The event codes in words, for the screen (ADM-038 showed `user.created`). */
export const NOTIFICATION_EVENT_LABELS: Record<string, string> = {
  ticket_assigned: 'Ticket assigned', ticket_resolved: 'Ticket resolved',
  wo_created: 'Work order created', wo_assigned: 'Work order assigned',
  wo_completed: 'Work order completed', lease_expiring: 'Lease expiring',
  visitor_checked_in: 'Visitor checked in', 'user.created': 'User created',
};
export const notificationEventLabel = (e: string) => NOTIFICATION_EVENT_LABELS[e] ?? e;
export const NOTIFICATION_CHANNEL_LABELS: Record<string, string> = {
  email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', push: 'Push',
};
export const TEMPLATE_SUBJECT_MAX = 200;
export const TEMPLATE_BODY_MAX = 5000;

export type TemplateField = 'event_type' | 'channel' | 'subject_template' | 'body_template';

/** Well-formedness only: balanced {{ }} and no empty {{}}. */
export function validateTemplatePlaceholders(text: string, label: string): string | null {
  const opens = (text.match(/\{\{/g) || []).length;
  const closes = (text.match(/\}\}/g) || []).length;
  if (opens !== closes) return `${label} has unbalanced {{ }} placeholders`;
  if (/\{\{\s*\}\}/.test(text)) return `${label} contains an empty {{}} placeholder`;
  return null;
}

export function validateTemplateText(
  value: string,
  label: string,
  minLen: number,
  maxLen: number,
): string | null {
  const text = (value || '').trim();
  if (!text) return `${label} is required`;
  if (text.length < minLen || text.length > maxLen) {
    return `${label} must be between ${minLen} and ${maxLen} characters`;
  }
  if (!/\p{L}/u.test(text)) return `${label} must contain at least one letter`;
  return validateTemplatePlaceholders(text, label);
}

export function validateEventType(value: string): string | null {
  const event = (value || '').trim();
  if (!event) return 'Event type is required';
  if (!NOTIFICATION_EVENTS.some((e) => e.value === event)) return `Unknown event type: ${event}`;
  return null;
}

export function validateTemplateChannel(value: string): string | null {
  if (!value) return 'Channel is required';
  return (NOTIFICATION_CHANNELS as readonly string[]).includes(value)
    ? null
    : `Unknown channel: ${value}`;
}

/** Subject is required for email only; other channels have no subject. */
export function validateTemplateSubject(value: string, channel: string): string | null {
  if (channel !== 'email') {
    const text = (value || '').trim();
    return text ? validateTemplatePlaceholders(text, 'Subject') : null;
  }
  return validateTemplateText(value, 'Subject', 2, TEMPLATE_SUBJECT_MAX);
}

/** Map a backend 4xx message onto the template field it refers to. */
export const TEMPLATE_ERROR_ROUTING: FieldErrorRouting<TemplateField> = {
  fromLoc: {
    event_type: 'event_type',
    channel: 'channel',
    subject_template: 'subject_template',
    body_template: 'body_template',
  },
  // "A <channel> template for '<event>' already exists. Edit that template instead."
  fromMessage: (m) => (m.includes('template for') ? 'event_type' : null),
};

// ---------------------------------------------------------------------------
// Sites / buildings (TC_137)
// ---------------------------------------------------------------------------

const SITE_CODE_ALLOWED = /^[A-Za-z0-9_-]+$/;
// Periods are allowed for abbreviations that are part of the real name —
// "St. John's", "St. Louis", "Washington D.C." The "at least one letter" check
// in validatePlaceName still rejects "...", and digits remain disallowed.
// Mirrors _PLACE_ALLOWED in backend/utils/validation.py — keep the two in sync.
const PLACE_ALLOWED = /^[A-Za-z .'-]+$/;
export const MAX_FLOORS = 200;
export const MAX_AREA_SQFT = 10_000_000;

export type SiteField =
  | 'name' | 'code' | 'city' | 'country' | 'total_area_sqft' | 'floors_count' | 'timezone';

/** Real IANA zone list where the browser exposes it, with a small fallback. */
export function ianaTimezones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  try {
    const zones = intl.supportedValuesOf?.('timeZone');
    // UTC is a real zone and every organisation's default (Organisation.
    // timezone), but supportedValuesOf leaves it out — so the Add Site form,
    // which now starts at the organisation's zone (R4-1), showed the first
    // option and refused "Unknown timezone: UTC" on save.
    if (zones?.length) return zones.includes('UTC') ? zones : [...zones, 'UTC'];
  } catch { /* fall through */ }
  return ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'America/New_York', 'UTC'];
}

export function validateSiteName(value: string): string | null {
  return validateDisplayName(value, 'Site name', 2, 100);
}

export function validateSiteCode(value: string): string | null {
  const code = (value || '').trim();
  if (!code) return 'Site code is required';
  if (code.length > 20) return 'Site code must be 20 characters or fewer';
  if (!SITE_CODE_ALLOWED.test(code)) {
    return 'Site code may only contain letters, digits, hyphens and underscores';
  }
  return null;
}

export function normaliseSiteCode(value: string): string {
  return (value || '').trim().toUpperCase();
}

/** City and country share one rule — country is free text in this codebase. */
export function validatePlaceName(value: string, label: string): string | null {
  const place = (value || '').replace(/\s+/g, ' ').trim();
  if (!place) return `${label} is required`;
  if (place.length < 2 || place.length > 85) return `${label} must be between 2 and 85 characters`;
  if (!/\p{L}/u.test(place)) return `${label} must contain at least one letter`;
  if (!PLACE_ALLOWED.test(place)) {
    return `${label} may only contain letters, spaces, periods, hyphens and apostrophes`;
  }
  return null;
}

export function validateFloors(value: string): string | null {
  const raw = (value || '').trim();
  if (!raw) return 'Floors is required';
  if (!/^\d+$/.test(raw)) return `Floors must be a whole number between 1 and ${MAX_FLOORS}`;
  const n = Number(raw);
  if (n < 1 || n > MAX_FLOORS) return `Floors must be a whole number between 1 and ${MAX_FLOORS}`;
  return null;
}

/** Area is optional; blank is fine, but a supplied value must be positive. */
export function validateAreaSqft(value: string): string | null {
  const raw = (value || '').trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 'Total area must be a number';
  if (n <= 0) return 'Total area must be greater than zero';
  if (n > MAX_AREA_SQFT) return `Total area must be ${formatNumber(MAX_AREA_SQFT, orgFormat())} sqft or less`;
  return null;
}

export function validateTimezone(value: string): string | null {
  const tz = (value || '').trim();
  if (!tz) return 'Timezone is required';
  return ianaTimezones().includes(tz) ? null : `Unknown timezone: ${tz}`;
}

/** Map a backend 4xx message onto the site field it refers to. */
export const SITE_ERROR_ROUTING: FieldErrorRouting<SiteField> = {
  fromLoc: {
    name: 'name',
    code: 'code',
    city: 'city',
    country: 'country',
    total_area_sqft: 'total_area_sqft',
    floors_count: 'floors_count',
    timezone: 'timezone',
  },
  // _reject_duplicate_site raises "A site code named '<value>' already exists."
  // or "A site named '<value>' already exists." Check the code phrase first —
  // "site named" is a substring of neither, but the order documents the intent.
  fromMessage: (m) => {
    if (m.includes('site code named')) return 'code';
    if (m.includes('site named')) return 'name';
    return null;
  },
};

// ---------------------------------------------------------------------------
// Contractor invite
// ---------------------------------------------------------------------------
// These mirror schemas/contractor.py:ContractorCreate exactly. They exist to
// catch a bad value before the request, not to add rules of their own — the
// invite form previously sent anything and surfaced the resulting 422 as
// "Request failed with status code 422" (TC_25).

export type ContractorField =
  | 'company_name' | 'trade' | 'gst_number' | 'pan_number'
  | 'contact_name' | 'contact_email' | 'contact_phone'
  | 'address' | 'city' | 'state' | 'pincode';

/** Indian PAN: five letters, four digits, one letter. */
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export function normalisePan(value: string): string {
  return value.toUpperCase().trim();
}

export function validatePan(value: string): string | null {
  const pan = normalisePan(value);
  if (!pan) return null;                       // optional in the schema
  if (pan.length > 10) return 'PAN must be 10 characters (e.g. ABCDE1234F)';
  if (!PAN_PATTERN.test(pan)) return 'Invalid PAN format. Expected: ABCDE1234F';
  return null;
}

/**
 * The contractor GSTIN (Deep-test CON-018): the tenant/organisation rule
 * (validateGstinStrict, below), and — when a PAN is typed too — the PAN inside
 * it (characters 3-12) must be that PAN. Mirrors schemas/contractor.py
 * _clean_gstin and utils/validation.py gstin_pan_mismatch, message for message.
 * This used to check length only, so 'XYZ' was saved.
 */
export function validateGstin(value: string, pan?: string | null): string | null {
  const strict = validateGstinStrict(value);
  if (strict) return strict;
  const gst = normaliseGstin(value);
  const p = (pan || '').trim().toUpperCase();
  if (gst && p && gst.slice(2, 12) !== p) {
    return `This GSTIN belongs to PAN ${gst.slice(2, 12)}, not ${p} — characters 3 to 12 `
      + "of a GSTIN are the holder's PAN.";
  }
  return null;
}

export function validateContractorEmail(value: string): string | null {
  const email = value.trim();
  if (!email) return null;                     // optional
  // Deliberately permissive: EmailStr on the server is the authority, and it
  // accepts forms a tighter pattern would reject (a display name, for one).
  // Only the failures the backend also rejects are caught here — an address
  // with no @, nothing either side of it, or a domain with no dot.
  const at = email.lastIndexOf('@');
  const local = at === -1 ? '' : email.slice(0, at);
  const domain = at === -1 ? '' : email.slice(at + 1);
  const domainHasDot = /[^.]\.[^.]/.test(domain);
  if (!local || !domain || !domainHasDot) {
    return 'Enter a valid email address';
  }
  return null;
}

export function validateContractorPhone(value: string): string | null {
  const phone = value.trim();
  if (!phone) return null;                     // optional
  if (phone.length > 20) return 'Phone must be 20 characters or fewer';
  return null;
}

export function validateCompanyName(value: string): string | null {
  const name = value.trim();
  if (!name) return 'Company name is required';
  if (name.length > 255) return 'Company name must be 255 characters or fewer';
  return null;
}

//: Maps a 422 loc back to the input that produced it, so the message lands on
//: the field rather than in a toast that names no field at all.
export const CONTRACTOR_ERROR_ROUTING: FieldErrorRouting<ContractorField> = {
  fromLoc: {
    company_name: 'company_name',
    trade: 'trade',
    gst_number: 'gst_number',
    pan_number: 'pan_number',
    contact_name: 'contact_name',
    contact_email: 'contact_email',
    contact_phone: 'contact_phone',
    address: 'address',
    city: 'city',
    state: 'state',
    pincode: 'pincode',
  },
  // A 422 for anything else keeps field === null, and the caller shows the
  // backend's own message rather than inventing a generic one.
  fromMessage: () => null,
};

// ---------------------------------------------------------------------------
// Tenant — Add and Edit
// ---------------------------------------------------------------------------
// Keys are the backend field names from schemas/tenant.py:TenantCreate and
// TenantUpdate, which share one modal on the front end.
//
// gst_number is why this exists (TC_25). The backend rejects a malformed GSTIN
// with a 422 naming body.gst_number, and both mutations reported it as
// `toast.error(e.message)` — the sentence was the backend's own and read well
// enough, but it floated above a form in which every field still looked
// accepted and the GST box was not marked at all.
//
// Only the fields the modal actually renders are listed. pan_number is on
// TenantUpdate and has no input here, so a 422 blaming it deliberately falls
// through to the toast rather than being routed at an input that does not
// exist.

export type TenantField =
  | 'company_name' | 'contact_name' | 'contact_email' | 'contact_phone'
  | 'gst_number' | 'billing_address' | 'allocated_desks';

/** Mirrors utils/validation.py:GSTIN_RE. */
const GSTIN_STRICT = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

/**
 * State codes 01–38, plus 97 (other territory) and 99 (UN bodies) — the same
 * set as utils/validation.py:GST_STATE_CODES. 99 really is valid; a probe
 * against production expecting it to be refused came back 200.
 */
const GST_STATE_CODES = new Set([
  ...Array.from({ length: 38 }, (_, i) => String(i + 1).padStart(2, '0')),
  '97', '99',
]);

/** Uppercased, trimmed, spaces dropped — as the backend normalises it. */
export function normaliseGstin(value: string): string {
  return (value || '').trim().toUpperCase().replace(/\s/g, '');
}

/**
 * The tenant and organisation GSTIN rule, mirroring utils/validation.py:
 * validate_gstin message for message, so the client and the server never
 * disagree about why a value was refused.
 *
 * validateGstin above (the contractor form) is this rule plus the PAN-inside
 * check. It used to be length-only, matching a contractor schema that was;
 * both were tightened together in Deep-test CON-018.
 *
 * Shape only. The 15th character is a checksum neither side computes — the
 * number is printed on an invoice, not used to authorise anything, and
 * rejecting a real GSTIN over a checksum bug is the worse failure.
 *
 * Returns null for an empty value: it is optional on both paths.
 */
export function validateGstinStrict(value: string): string | null {
  const v = normaliseGstin(value);
  if (!v) return null;
  if (v.length !== 15) return `A GSTIN is 15 characters; this has ${v.length}`;
  if (!GSTIN_STRICT.test(v)) {
    return 'GSTIN must look like 27AAAAA0000A1Z5 — two digits, a ten-character '
      + 'PAN, an entity digit, the letter Z, then a checksum character';
  }
  if (!GST_STATE_CODES.has(v.slice(0, 2))) {
    return `'${v.slice(0, 2)}' is not a valid GST state code`;
  }
  return null;
}

export const TENANT_ERROR_ROUTING: FieldErrorRouting<TenantField> = {
  fromLoc: {
    company_name: 'company_name',
    contact_name: 'contact_name',
    contact_email: 'contact_email',
    contact_phone: 'contact_phone',
    gst_number: 'gst_number',
    billing_address: 'billing_address',
    allocated_desks: 'allocated_desks',
  },
  // The duplicate-name 409 carries a sentence, not a loc, and is left to the
  // toast on purpose: it already names the clashing tenant and is not a
  // complaint about what was typed into the box.
  fromMessage: () => null,
};

// ---------------------------------------------------------------------------
// Helpdesk ticket
// ---------------------------------------------------------------------------
// Keys are the backend field names from schemas/helpdesk.py:TicketCreate, so a
// 422 lands on the input that caused it instead of a toast reading "Request
// failed with status code 422" (TC_27). No value lists here — the categories
// come from GET /helpdesk/ticket-categories, and duplicating them locally is
// the exact defect this replaces.

export type TicketField =
  | 'title' | 'description' | 'category' | 'priority'
  | 'location_building_id' | 'location_floor_id' | 'location_detail';

export const TICKET_ERROR_ROUTING: FieldErrorRouting<TicketField> = {
  fromLoc: {
    title: 'title',
    description: 'description',
    category: 'category',
    priority: 'priority',
    location_building_id: 'location_building_id',
    location_floor_id: 'location_floor_id',
    location_detail: 'location_detail',
  },
  // Nothing message-based: the ticket endpoints answer with Pydantic's own
  // 422 array, which fromLoc already resolves.
  fromMessage: () => null,
};

// ---------------------------------------------------------------------------
// FastAPI 422 detail -> a sentence a user can act on
// ---------------------------------------------------------------------------

/**
 * Acronyms and units that must not be sentence-cased into nonsense.
 *
 * Derived from the real distribution, not guessed: of the 699 distinct field
 * names reachable in a `loc` across 306 request-body schemas, 122 humanise
 * badly with a plain underscore-to-space rule. These ~35 tokens fix all of
 * them, which is why there is no per-field override map — the problem was
 * suffixes, not one-offs.
 */
const LOC_TOKENS: Record<string, string> = {
  id: 'ID', ids: 'IDs', url: 'URL', urls: 'URLs', api: 'API', csv: 'CSV',
  pdf: 'PDF', svg: 'SVG', json: 'JSON', inr: '', kwh: 'kWh', pct: '%',
  sqft: 'sq ft', gst: 'GST', pan: 'PAN', sla: 'SLA', wo: 'work order',
  ppm: 'PPM', nps: 'NPS', av: 'AV', hse: 'HSE', kyc: 'KYC', msa: 'MSA',
  dsar: 'DSAR', rfc: 'RFC', cab: 'CAB', esg: 'ESG', iot: 'IoT', bms: 'BMS',
  qr: 'QR', ev: 'EV', km: 'km', hrs: 'hours', qty: 'quantity',
  mins: 'minutes', pos: 'position',
};

/** `x_pos` -> "X position", `booking_id` -> "Booking ID", `kwh_generated` -> "kWh generated". */
export function humaniseFieldName(field: string): string {
  // An empty token is DROPPED: `_inr` names a column, not the currency an
  // organisation uses, so "hourly_rate_inr" reads "Hourly rate" rather than
  // claiming rupees to a Dubai organisation (Bug_62 sweep).
  const parts = field.split('_').map((p) => LOC_TOKENS[p.toLowerCase()] ?? p).filter((p) => p !== '');
  const joined = parts.join(' ').trim();
  if (!joined) return field;
  // Only sentence-case when the first token was NOT a known one, so "kWh" and
  // "km" keep the casing they were given.
  const firstWasToken = LOC_TOKENS[(field.split('_')[0] ?? '').toLowerCase()] !== undefined;
  return firstWasToken ? joined : joined.charAt(0).toUpperCase() + joined.slice(1);
}

/**
 * Pydantic error types where the message alone tells the user nothing.
 *
 * "Field required" names no field; "Input should be a valid integer" names no
 * field. These get the field name prefixed. Everything else — value_error from
 * our own validators, pattern mismatches — is left BYTE-IDENTICAL, because
 * those messages were written deliberately and already read as sentences.
 * Prefixing "Cannot move an allocation request from 'pending' to 'fulfilled'"
 * with "Status: " would make it worse.
 */
const STRUCTURAL_ERROR = /^(missing|extra_forbidden|.*_parsing|.*_type|too_short|too_long|string_too_short|string_too_long|greater_than.*|less_than.*)$/;

/** Detail entries as FastAPI emits them for a 422. */
interface DetailItem {
  msg?: unknown;
  loc?: unknown;
  type?: unknown;
  ctx?: unknown;
}

/**
 * One entry of a 422 detail, rendered for a human.
 *
 * `loc` is ["body", "room_label"] for a flat body and
 * ["body", "positions", 3, "x_fraction"] for an array one — 11 endpoints take
 * array bodies, so the index matters: without it "X fraction is required" gives
 * no clue which of 1,000 items failed.
 */
export function formatDetailItem(item: DetailItem): string | null {
  const raw = item && typeof item === 'object' && 'msg' in item ? String(item.msg) : null;
  if (!raw) return null;
  // Pydantic prefixes custom validator messages with "Value error, ".
  const msg = raw.replace(/^Value error,\s*/, '');

  const type = typeof item.type === 'string' ? item.type : '';
  if (!STRUCTURAL_ERROR.test(type)) return msg;

  const loc = Array.isArray(item.loc) ? item.loc : [];
  // Drop the source segment: body / query / path / header / cookie.
  const path = loc.slice(1);
  const field = [...path].reverse().find((p) => typeof p === 'string');
  if (!field) return msg;

  const index = path.find((p) => typeof p === 'number');
  const label = humaniseFieldName(String(field));
  // Deep-test CON-011: two library sentences that said nothing a person can
  // act on. An id that is not an id came from a picker left empty ("Building
  // ID: Input should be a valid UUID, invalid length: expected length 32…"),
  // so the person is asked to choose one; a blank required text field ("Name:
  // String should have at least 1 character") is required. A real minimum
  // above one still says the number.
  const ctx = (item.ctx && typeof item.ctx === 'object' ? item.ctx : {}) as { min_length?: unknown };
  const plain =
    type === 'uuid_parsing'
      ? `Choose a ${label.replace(/ ID$/, '').toLowerCase()}`
      : type === 'string_too_short' && ctx.min_length === 1
        ? `${label} is required`
        : type === 'string_too_short' && typeof ctx.min_length === 'number'
          ? `${label} must be at least ${ctx.min_length} characters`
          : null;
  if (plain) return index === undefined ? plain : `Item ${(index as number) + 1}: ${plain}`;
  // "Extra inputs are not permitted" does not say WHICH input, which is
  // useless precisely when it fires: a body that forbids extras is rejecting a
  // field name the caller believes in.
  const sentence =
    /^field required$/i.test(msg) ? `${label} is required`
      : type === 'extra_forbidden' ? `${label} is not a field this endpoint accepts`
        : `${label}: ${msg}`;
  return index === undefined ? sentence : `Item ${(index as number) + 1}: ${sentence}`;
}

/** Join a whole 422 detail array into one message. */
export function formatDetailList(detail: unknown[]): string {
  return detail
    .map((d) => formatDetailItem(d as DetailItem))
    .filter((m): m is string => Boolean(m))
    .join('; ');
}

// ---------------------------------------------------------------------------
// Numeric form fields
// ---------------------------------------------------------------------------

/**
 * Parse a numeric form field, returning either a value or a reason it is not one.
 *
 * The pattern this replaces is `parseInt(raw) || fallback`, which was the cause
 * of Spaces TC_33: zero is falsy in JavaScript, so a user who typed 0 — under a
 * placeholder that told them to — silently sent 1. Neither production database
 * held a single floor at level 0.
 *
 * Two rules, both learned from that bug:
 *   - An empty or unparseable field is an ERROR, never a default. Guessing a
 *     number on someone's behalf is how a wrong value gets persisted quietly.
 *   - 0 and negatives are values like any other. Only `min`/`max` may reject
 *     them, and those bounds should mirror the endpoint's own constraints so
 *     the form and the API cannot disagree.
 */
export function parseNumericInput(
  raw: string,
  opts: { label: string; min?: number; max?: number; integer?: boolean },
): { ok: true; value: number } | { ok: false; error: string } {
  const trimmed = (raw ?? '').trim();
  if (trimmed === '') return { ok: false, error: `${opts.label} is required` };
  // Always parse as a float, even when an integer is wanted: Number.parseInt('2.5')
  // is 2, so parsing as an int here would silently truncate a decimal into a
  // plausible value — the same silent-coercion failure this helper exists to end.
  const n = Number.parseFloat(trimmed);
  // Number.parseFloat('12abc') is 12, which would silently accept a typo. Require
  // the whole string to be a number.
  if (!Number.isFinite(n) || !/^-?\d*\.?\d+$/.test(trimmed)) {
    return { ok: false, error: `${opts.label} must be a number` };
  }
  if (opts.integer && !Number.isInteger(n)) {
    return { ok: false, error: `${opts.label} must be a whole number` };
  }
  if (opts.min !== undefined && n < opts.min) {
    return { ok: false, error: `${opts.label} must be at least ${opts.min}` };
  }
  if (opts.max !== undefined && n > opts.max) {
    return { ok: false, error: `${opts.label} must be at most ${opts.max}` };
  }
  return { ok: true, value: n };
}

/**
 * Validate several numeric fields, returning the first error or all values.
 * Callers show one message at a time, so reporting the first is what the UI needs.
 */
export function parseNumericFields<K extends string>(
  fields: Record<K, { raw: string; label: string; min?: number; max?: number; integer?: boolean }>,
): { ok: true; values: Record<K, number> } | { ok: false; error: string } {
  const values = {} as Record<K, number>;
  for (const key of Object.keys(fields) as K[]) {
    const f = fields[key];
    const r = parseNumericInput(f.raw, { label: f.label, min: f.min, max: f.max, integer: f.integer });
    if (!r.ok) return { ok: false, error: r.error };
    values[key] = r.value;
  }
  return { ok: true, values };
}

// ---------------------------------------------------------------------------
// Phone numbers
// ---------------------------------------------------------------------------
//
// Mirrors backend/utils/validation.py — keep the two in sync.
//
// Deliberately NOT "exactly ten digits". Measured against live data a
// ten-digit rule rejects five legitimate flexpack rows, and flexpack's
// organisations are Asia/Dubai, where a national mobile is nine digits and a
// landline is seven plus an area code. 7-15 digits is ITU-T E.164.
//
// The old client rules counted CHARACTERS, which is why `abcdefghij` passed:
// ten characters, zero digits.

/** Separators people actually type. Stripped, not rejected. */
const PHONE_SEPARATORS = /[ \-(). ‐-―]/g;
const PHONE_ALLOWED = /^\+?\d{7,15}$/;

export const PHONE_ERROR =
  'Enter a valid phone number — 7 to 15 digits, optionally starting with +. ' +
  'Spaces, dashes and brackets are fine.';

/** Strip separators, keep a leading +. Blank stays blank. */
export function normalisePhone(value: string | null | undefined): string {
  return (value ?? '').trim().replace(PHONE_SEPARATORS, '');
}

/** null when acceptable, an error string when not. Blank IS acceptable —
 *  phone is optional on every path, and the layers now agree on that. */
export function validatePhone(value: string | null | undefined): string | null {
  const normalised = normalisePhone(value);
  if (normalised === '') return null;
  return PHONE_ALLOWED.test(normalised) ? null : PHONE_ERROR;
}

/** The zod shape for an optional phone field. Every form uses this rather than
 *  its own `min(10)`, which is how the app ended up with five different rules
 *  and one that checked characters instead of digits. */
export const phoneSchema = z
  .string()
  .optional()
  .or(z.literal(''))
  .refine((v) => validatePhone(v) === null, PHONE_ERROR);

// ---------------------------------------------------------------------------
// Person names
// ---------------------------------------------------------------------------
//
// Mirrors validate_person_name() in backend/utils/validation.py.
//
// \p{L} covers letters in every script; \p{M} is the piece that was missing on
// the server — Devanagari vowel signs and viramas are marks, not letters, so a
// letters-only test rejects भारत and தமிழ். Both apostrophes are allowed
// because U+2019 is what a phone keyboard actually emits for D’Souza.
//
// Digits are rejected outright, not just when a name is entirely numeric:
// "entirely numeric" lets John3 and Test9999 through, and both are in
// production today.

const PERSON_NAME_ALLOWED = /^[\p{L}\p{M} .'’ʼ-]+$/u;
const PERSON_NAME_HAS_LETTER = /\p{L}/u;
export const PERSON_NAME_MAX = 128;

export function normalisePersonName(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

export function validatePersonName(
  value: string | null | undefined,
  label = 'Name',
): string | null {
  const name = normalisePersonName(value);
  if (!name) return `${label} is required`;
  if (name.length > PERSON_NAME_MAX) {
    return `${label} must be between 1 and ${PERSON_NAME_MAX} characters`;
  }
  if (!PERSON_NAME_HAS_LETTER.test(name)) {
    return `${label} must contain at least one letter`;
  }
  if (!PERSON_NAME_ALLOWED.test(name)) {
    return `${label} may only contain letters, spaces and . ' -`;
  }
  return null;
}

/** The zod shape for a required person name. Every form uses this rather than
 *  its own min(1), which is how `12345` passed on all eleven of them. */
export const personNameSchema = (label = 'Name') =>
  z.string().refine((v) => validatePersonName(v, label) === null, (v) => ({
    // Derived, not fixed. A fixed charset message told someone who left the
    // box empty that their name "may only contain letters, spaces and . ' -",
    // which does not describe what they did wrong. validatePersonName already
    // knows which rule failed, so it supplies the wording too.
    message: validatePersonName(v, label) ?? `${label} is invalid`,
  }));

/** A person name on a nullable field: blank is allowed and means "not set".
 *
 *  `tenants.contact_name` and `contractors.contact_name` are the only two —
 *  both nullable, both a person rather than the company beside them, and both
 *  length-only until now. Mirrors NullablePersonName in utils/validation.py. */
export function validateNullablePersonName(
  value: string | null | undefined,
  label = 'Contact name',
): string | null {
  if (!normalisePersonName(value)) return null;
  return validatePersonName(value, label);
}

// ---------------------------------------------------------------------------
// Generate Invoices — the billing period
// ---------------------------------------------------------------------------
// Mirrors backend/schemas/tenant.py:BillingPeriodRules message for message, the
// way validateGstinStrict mirrors validate_gstin. Two copies of a rule that
// disagree about WHY something was refused is worse than one copy and a round
// trip, so the wording is copied exactly and pinned by tests on both sides.
//
// The backend raises these from a model_validator, so its 422 carries
// loc ['body'] with no field in it. That is why this exists: without a client
// rule there is nothing to put on an input, and the message can only be a toast
// floating above three date boxes that all look accepted.
//
// Order matters. The backend checks ordering first (it is a base-class
// validator), then the due date, then the span, and reports only the first
// failure. Anything else here would show a different message than the server
// would for the same input.

export const MAX_BILLING_PERIOD_DAYS = 366;

export type BillingPeriodField =
  | 'billing_period_start' | 'billing_period_end' | 'due_date';

export interface BillingPeriodProblem {
  /** Every input to mark. More than one when the fault is not attributable. */
  fields: BillingPeriodField[];
  message: string;
}

/** A date in these sentences: "16 Sep 2026", the backend's range_point
 *  (Deep-test R5-3). Pinned to day-month-year rather than the reader's
 *  pattern, because the sentence is the server's and is compared with it
 *  word for word. */
const inWords = (iso: string) => displayDateLong(iso, { pattern: 'DD/MM/YYYY' });

/** Whole days between two yyyy-mm-dd strings, as python's (end - start).days. */
function daysBetween(start: string, end: string): number {
  const MS = 24 * 60 * 60 * 1000;
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / MS);
}

export function validateBillingPeriod(
  start: string, end: string, due: string,
): BillingPeriodProblem | null {
  // An empty box is the browser's business, not this rule's — and the backend
  // reports a missing date as its own field-level 422.
  if (!start || !end || !due) return null;

  if (end < start) {
    // Equal dates are legal: a one-day billing period is a real thing.
    return {
      fields: ['billing_period_end'],
      message: `billing period ends before it starts: ${inWords(start)} to ${inWords(end)}`,
    };
  }
  if (due < end) {
    return {
      fields: ['due_date'],
      message: `due date ${inWords(due)} is before the end of the billing period `
        + `(${inWords(end)}); the invoice would be overdue on issue`,
    };
  }
  const span = daysBetween(start, end);
  if (span > MAX_BILLING_PERIOD_DAYS) {
    // "check the year on both dates" — so both are marked. Which of the two
    // carries the typo is not knowable from the values alone.
    return {
      fields: ['billing_period_start', 'billing_period_end'],
      message: `billing period spans ${span} days `
        + `(maximum ${MAX_BILLING_PERIOD_DAYS}); check the year on both dates`,
    };
  }
  return null;
}

/**
 * A 422 from these endpoints that DOES name a field — a missing or unparseable
 * date — lands on it. The rule violations above never will: they come from a
 * model_validator whose loc is ['body'], and guessing a field from the message
 * text is the substring-matching mistake this whole module exists to avoid.
 * Those keep field === null and the caller shows them against the form.
 */
export const BILLING_ERROR_ROUTING: FieldErrorRouting<BillingPeriodField> = {
  fromLoc: {
    billing_period_start: 'billing_period_start',
    billing_period_end: 'billing_period_end',
    due_date: 'due_date',
  },
  fromMessage: () => null,
};

// ---------------------------------------------------------------------------
// Vehicle numbers
// ---------------------------------------------------------------------------

/**
 * The placeholder both forms show.
 *
 * 🔴 They disagreed: the visitor form taught `KA 01 AB 1234` and the parking
 * form `e.g. HR-26-AB-1234`, and the live data followed — spaces in visits,
 * hyphens in parking bookings. One identifier, two conventions, taught by the
 * product itself. No plate is in both tables yet, so nothing has collided; the
 * point is to stop teaching the divergence.
 */
export const VEHICLE_NUMBER_PLACEHOLDER = 'e.g. KA 01 AB 1234';

/** backend/schemas/vehicle_number.MAX_VEHICLE_NUMBER, and the column width. */
export const MAX_VEHICLE_NUMBER = 20;

/** The server's sentence, mirrored exactly — schemas/vehicle_number._REJECTED. */
export const VEHICLE_NUMBER_MESSAGE =
  'A vehicle number needs at least one letter or number — it is what you would '
  + 'read off the plate. The format itself is not checked: there is no single '
  + 'one, and a rule tight enough to catch a typo would refuse a real registration.';

/**
 * Trim, upper-case, empty becomes null. Mirrors normalise_vehicle_number.
 *
 * 🔴 NO PLATE REGEX, AND NO SEPARATOR STRIPPING. India alone has several live
 * formats — KA 01 AB 1234, MH-12-A-123, BH-series, temporary and trade plates —
 * and a visitor may arrive in a foreign-registered or diplomatic vehicle. Any
 * pattern tight enough to reject "&*^%" on structure rejects somebody's actual
 * car, and the cost of that is a real visitor turned away at a barrier.
 *
 * Nothing matches on this field anywhere in the product — no ANPR, no gate
 * lookup, no join between a visit and a parking booking — so a format check
 * would be a spelling test on a value nothing resolves.
 *
 * Case is the ONE exception, because no registration's lower-case letters mean
 * anything different. It removes one axis of divergence and not the others:
 * "KA 01 AB 1234" and "KA-01-AB-1234" remain two records, which is tolerable
 * only because nothing joins them. If a join is ever wanted it needs a vehicle
 * RECORD with an id, not more string normalisation.
 */
export function normaliseVehicleNumber(value: string | null | undefined): string | null {
  const cleaned = (value ?? '').trim().toUpperCase();
  return cleaned || null;
}

/** null when acceptable (including absent), else the message to show. */
export function validateVehicleNumber(value: string | null | undefined): string | null {
  const cleaned = normaliseVehicleNumber(value);
  if (cleaned === null) return null;                 // absent is not invalid
  if (cleaned.length > MAX_VEHICLE_NUMBER) {
    return `A vehicle number may be at most ${MAX_VEHICLE_NUMBER} characters`;
  }
  return /[A-Z0-9]/.test(cleaned) ? null : VEHICLE_NUMBER_MESSAGE;
}
